/**
 * Export Utilities
 *
 * This module provides functions to export project data in various formats:
 * - Fountain (.fountain) - Screenplay script format
 * - EDL (.edl) - Edit Decision List for NLE software
 * - CSV (.csv) - Beat list for spreadsheets
 * - Image Sequence - Sequential numbered images
 *
 * All exports respect the "shelf" status (exclude shelved beats) and use
 * timeline order (sorted by x-position).
 */

import type { BeatNode, AspectRatio } from '@/types';
import { imageStore } from './browserAPI';
import { fitImageToAspectRatio, blobToDataURL } from './imageProcessing';

// =============================================================================
// TYPES
// =============================================================================

export interface ExportOptions {
  /** Project name (used for filenames) */
  projectName: string;
  /** Frames per second from project settings */
  fps: number;
  /** Video aspect ratio for image processing */
  aspectRatio: AspectRatio;
  /** All nodes (will be filtered and sorted internally) */
  nodes: BeatNode[];
}

export interface ExportResult {
  success: boolean;
  error?: string;
  /** Content for single-file exports */
  content?: string;
  /** Files for multi-file exports (e.g., images + EDL) */
  files?: Array<{
    name: string;
    content: string | Blob;
    type: 'text' | 'blob';
  }>;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get timeline nodes (exclude shelved beats, sort by x-position)
 */
function getTimelineNodes(nodes: BeatNode[]): BeatNode[] {
  return nodes
    .filter((node) => !node.data.onShelf)
    .sort((a, b) => a.position.x - b.position.x);
}

/**
 * Convert seconds to timecode string (HH:MM:SS:FF)
 */
function secondsToTimecode(seconds: number, fps: number): string {
  const totalFrames = Math.floor(seconds * fps);
  const frames = totalFrames % fps;
  const totalSeconds = Math.floor(totalFrames / fps);
  const secs = totalSeconds % 60;
  const mins = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

/**
 * Sanitize filename (remove invalid characters)
 */
function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '_').trim();
}

// =============================================================================
// FOUNTAIN EXPORT
// =============================================================================

/**
 * Export project as Fountain screenplay format
 *
 * Fountain is a plain text markup language for screenwriting.
 * It's human-readable and supported by many screenwriting apps.
 *
 * @see https://fountain.io/
 */
export function exportToFountain(options: ExportOptions): ExportResult {
  try {
    const timelineNodes = getTimelineNodes(options.nodes);

    if (timelineNodes.length === 0) {
      return {
        success: false,
        error: 'No beats in timeline to export',
      };
    }

    const lines: string[] = [];

    // Title page
    lines.push(`Title: ${options.projectName}`);
    lines.push(`Draft date: ${new Date().toLocaleDateString()}`);
    lines.push('');
    lines.push('');

    // Process each beat
    for (let i = 0; i < timelineNodes.length; i++) {
      const node = timelineNodes[i];
      const { sceneHeading, script } = node.data;

      // Scene heading (if present)
      if (sceneHeading) {
        lines.push(sceneHeading.toUpperCase());
        lines.push('');
      }

      // Script content
      if (script) {
        lines.push(script);
        lines.push('');
      }
    }

    return {
      success: true,
      content: lines.join('\n'),
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export Fountain: ${(error as Error).message}`,
    };
  }
}

// =============================================================================
// PLAIN TEXT SCRIPT EXPORT
// =============================================================================

/**
 * Export project as plain text script
 *
 * Simple text format with scene headings and script content.
 * Good for sharing or printing without specialized software.
 */
export function exportToPlainText(options: ExportOptions): ExportResult {
  try {
    const timelineNodes = getTimelineNodes(options.nodes);

    if (timelineNodes.length === 0) {
      return {
        success: false,
        error: 'No beats in timeline to export',
      };
    }

    const lines: string[] = [];

    // Title
    lines.push(options.projectName.toUpperCase());
    lines.push('='.repeat(options.projectName.length));
    lines.push('');

    // Process each beat
    for (let i = 0; i < timelineNodes.length; i++) {
      const node = timelineNodes[i];
      const { sceneHeading, script } = node.data;

      // Scene heading (if present)
      if (sceneHeading) {
        lines.push(sceneHeading.toUpperCase());
        lines.push('');
      }

      // Script content
      if (script) {
        lines.push(script);
        lines.push('');
      }
    }

    return {
      success: true,
      content: lines.join('\n'),
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export plain text: ${(error as Error).message}`,
    };
  }
}

// =============================================================================
// EDL EXPORT
// =============================================================================

/**
 * Export project as EDL (Edit Decision List)
 *
 * EDL is a text format used to exchange timeline information between
 * video editing systems. We use CMX 3600 format, which is the most common.
 *
 * Format:
 * TITLE: [Project Name]
 * FCM: NON-DROP FRAME
 *
 * 001  AX       V     C        00:00:00:00 00:00:03:12 00:00:00:00 00:00:03:12
 * * FROM CLIP NAME: beat_001_opening_shot
 *
 * Event# Reel  Track Trans   Source In   Source Out  Rec In      Rec Out
 */
export function exportToEDL(options: ExportOptions): ExportResult {
  try {
    const timelineNodes = getTimelineNodes(options.nodes);

    if (timelineNodes.length === 0) {
      return {
        success: false,
        error: 'No beats in timeline to export',
      };
    }

    const lines: string[] = [];

    // Header
    lines.push(`TITLE: ${options.projectName}`);
    lines.push('FCM: NON-DROP FRAME');
    lines.push('');

    // Process each beat
    let currentTime = 0;
    for (let i = 0; i < timelineNodes.length; i++) {
      const node = timelineNodes[i];
      const eventNum = String(i + 1).padStart(3, '0');
      const duration = node.data.duration;

      // Source timecode (always starts at 00:00:00:00 for still images)
      const sourceIn = '00:00:00:00';
      const sourceOut = secondsToTimecode(duration, options.fps);

      // Record timecode (position in timeline)
      const recIn = secondsToTimecode(currentTime, options.fps);
      currentTime += duration;
      const recOut = secondsToTimecode(currentTime, options.fps);

      // Event line: Event# Reel Track Trans SourceIn SourceOut RecIn RecOut
      // AX = reel name, V = video track, C = cut transition
      lines.push(`${eventNum}  AX       V     C        ${sourceIn} ${sourceOut} ${recIn} ${recOut}`);

      // Clip name (sanitized beat title)
      const clipName = sanitizeFilename(`${eventNum}_${node.data.title}`);
      lines.push(`* FROM CLIP NAME: ${clipName}`);

      // Optional: Add source file reference (image path)
      if (node.data.imagePath) {
        lines.push(`* SOURCE FILE: images/${clipName}.png`);
      }

      lines.push('');
    }

    return {
      success: true,
      content: lines.join('\n'),
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export EDL: ${(error as Error).message}`,
    };
  }
}

// =============================================================================
// CSV EXPORT
// =============================================================================

/**
 * Export beat list as CSV (spreadsheet format)
 *
 * Useful for:
 * - Shot lists
 * - Production planning
 * - Script supervision
 * - Data analysis
 */
export function exportToCSV(options: ExportOptions): ExportResult {
  try {
    const timelineNodes = getTimelineNodes(options.nodes);

    if (timelineNodes.length === 0) {
      return {
        success: false,
        error: 'No beats in timeline to export',
      };
    }

    const lines: string[] = [];

    // Header row
    lines.push(
      '"Beat #","Title","Scene Heading","Duration (sec)","Duration (frames)","Timecode In","Timecode Out","Has Image","Script Preview"'
    );

    // Data rows
    let currentTime = 0;
    for (let i = 0; i < timelineNodes.length; i++) {
      const node = timelineNodes[i];
      const beatNum = i + 1;
      const duration = node.data.duration;
      const durationFrames = Math.floor(duration * options.fps);

      const timecodeIn = secondsToTimecode(currentTime, options.fps);
      currentTime += duration;
      const timecodeOut = secondsToTimecode(currentTime, options.fps);

      const hasImage = node.data.imagePath ? 'Yes' : 'No';

      // Script preview (first 50 chars, escape quotes)
      const scriptPreview = node.data.script
        .slice(0, 50)
        .replace(/"/g, '""')
        .replace(/\n/g, ' ');

      // Escape fields that might contain commas or quotes
      const title = `"${node.data.title.replace(/"/g, '""')}"`;
      const sceneHeading = `"${node.data.sceneHeading.replace(/"/g, '""')}"`;
      const script = `"${scriptPreview}"`;

      lines.push(
        `${beatNum},${title},${sceneHeading},${duration},${durationFrames},"${timecodeIn}","${timecodeOut}",${hasImage},${script}`
      );
    }

    return {
      success: true,
      content: lines.join('\n'),
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export CSV: ${(error as Error).message}`,
    };
  }
}

// =============================================================================
// IMAGE SEQUENCE EXPORT
// =============================================================================

/**
 * Export images as sequential numbered files
 *
 * Returns an array of files that need to be written:
 * - 001_beat_title.png
 * - 002_beat_title.png
 * - etc.
 *
 * For browser mode: Will need to fetch blob URLs and create downloadable files
 * For Electron mode: Will need to copy files from project images folder
 */
export async function exportImageSequence(
  options: ExportOptions
): Promise<ExportResult> {
  try {
    const timelineNodes = getTimelineNodes(options.nodes);

    if (timelineNodes.length === 0) {
      return {
        success: false,
        error: 'No beats in timeline to export',
      };
    }

    const files: Array<{
      name: string;
      content: string | Blob;
      type: 'text' | 'blob';
    }> = [];

    // Process each beat
    for (let i = 0; i < timelineNodes.length; i++) {
      const node = timelineNodes[i];
      const imagePath = node.data.imagePath;

      if (!imagePath) {
        console.warn(`Beat ${i + 1} (${node.data.title}) has no image, skipping`);
        continue;
      }

      // Generate sequential filename
      const num = String(i + 1).padStart(3, '0');
      const sanitizedTitle = sanitizeFilename(node.data.title);
      const filename = `${num}_${sanitizedTitle}.png`;

      // Get image blob from imageStore (browser mode) or path (Electron mode)
      const blobUrl = imageStore.get(imagePath);

      if (blobUrl) {
        // Browser mode: Process image with aspect ratio and offset
        try {
          const offsetX = node.data.imageOffsetX || 0;
          const offsetY = node.data.imageOffsetY || 0;
          const scale = node.data.imageScale || 1;

          // Apply aspect ratio fitting and offset
          const processedBlob = await fitImageToAspectRatio(
            blobUrl,
            options.aspectRatio,
            1280, // Max width for export
            offsetX,
            offsetY,
            scale
          );

          files.push({
            name: filename,
            content: processedBlob,
            type: 'blob',
          });
        } catch (error) {
          console.error(`Failed to process image for ${imagePath}:`, error);
          // Continue with other images
        }
      } else {
        // Electron mode: Will be handled by IPC
        // For now, just record the filename and original path
        files.push({
          name: filename,
          content: imagePath, // Original path, Electron will copy
          type: 'text',
        });
      }
    }

    if (files.length === 0) {
      return {
        success: false,
        error: 'No images found to export',
      };
    }

    return {
      success: true,
      files,
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export image sequence: ${(error as Error).message}`,
    };
  }
}

// =============================================================================
// COMBINED EDL + IMAGES EXPORT
// =============================================================================

/**
 * Export EDL with accompanying image sequence
 *
 * Creates a bundle with:
 * - ProjectName.edl
 * - images/001_beat_title.png
 * - images/002_beat_title.png
 * - etc.
 *
 * This is the most useful format for NLE import.
 */
export async function exportEDLWithImages(
  options: ExportOptions
): Promise<ExportResult> {
  try {
    // Generate EDL
    const edlResult = exportToEDL(options);
    if (!edlResult.success || !edlResult.content) {
      return edlResult;
    }

    // Generate image sequence
    const imagesResult = await exportImageSequence(options);
    if (!imagesResult.success || !imagesResult.files) {
      return imagesResult;
    }

    // Combine into a single result
    const files: Array<{
      name: string;
      content: string | Blob;
      type: 'text' | 'blob';
    }> = [
      {
        name: `${sanitizeFilename(options.projectName)}.edl`,
        content: edlResult.content,
        type: 'text',
      },
      // Prefix image files with images/ folder
      ...imagesResult.files.map((file) => ({
        ...file,
        name: `images/${file.name}`,
      })),
    ];

    return {
      success: true,
      files,
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to export EDL with images: ${(error as Error).message}`,
    };
  }
}
